import * as focusTrap from "focus-trap";

// Ui.Modal renders this element only while the modal is visible, so connecting
// is opening and disconnecting is closing.

// Shared so focus-trap keeps only the topmost trap active.
const trapStack = [];

const openModals = [];

// The node Ui.Modal's header renders into. Elm cannot mint its id: two open
// modals each need one of their own.
const TITLE = "[data-modal-title]";

let modalIds = 0;

const MANAGED_TITLE = /^elm-modal-\d+-title$/;

function topmost() {
  return openModals[openModals.length - 1];
}

// Everything off the path from the modal up to the body: a reader's cursor does
// not follow focus, so trapping Tab alone leaves it reading the page behind.
// A modal lower in the stack is spared — inerting it leaves the trap underneath
// with nothing tabbable, which focus-trap throws over.
function siblingsAbove(modal) {
  const siblings = [];
  const holdsOpenModal = (node) => openModals.some((open) => node === open || node.contains(open));

  for (let node = modal; node && node !== document.body; node = node.parentNode) {
    if (!node.parentNode) break;

    for (const sibling of node.parentNode.children) {
      if (sibling === node || sibling.hasAttribute("inert")) continue;
      if (holdsOpenModal(sibling)) continue;

      siblings.push(sibling);
    }
  }

  return siblings;
}

let inerted = [];

// Only the topmost modal hides the page, so closing it restores what it hid.
function isolateTopmost() {
  // The attribute, not the property: `[inert]` is what CSS and queries see.
  for (const element of inerted) element.removeAttribute("inert");

  const modal = topmost();
  inerted = modal ? siblingsAbove(modal) : [];

  for (const element of inerted) element.setAttribute("inert", "");
}

// Escape belongs to the dialog, and only the topmost one answers.
document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape" || event.defaultPrevented) return;

  const modal = topmost();
  if (!modal || modal.dataset.dismissible === "false") return;

  event.preventDefault();
  modal.dispatchEvent(new CustomEvent("close"));
});

// A click on another <elm-modal> is "outside" this trap, so without allowing it
// each active trap would preventDefault the other modal's controls.
function isInAnyModal(node) {
  return node.closest("elm-modal") != null;
}

class ElmModal extends HTMLElement {
  connectedCallback() {
    this.returnFocusTo = document.activeElement;
    this.modalId ??= `elm-modal-${++modalIds}`;
    openModals.push(this);
    isolateTopmost();

    this.wire();
    // Elm paints the panel a frame later and re-renders it in place after that.
    this.observer = new MutationObserver(() => this.wire());
    this.observer.observe(this, { childList: true, subtree: true });

    // Recorded while we are still in the document: by the time we disconnect,
    // activeElement is the body.
    this.heldFocus = false;
    this.onFocusIn = () => {
      this.heldFocus = true;
    };
    this.addEventListener("focusin", this.onFocusIn);

    this.trap = focusTrap.createFocusTrap(this, {
      allowOutsideClick: (event) => isInAnyModal(event.target),
      // Escape closes through Elm, which is what removes this element.
      escapeDeactivates: false,
      // disconnectedCallback decides where focus goes.
      returnFocusOnDeactivate: false,
      // The dialog, so a reader hears its name before the backdrop's dismiss.
      initialFocus: () => this.dialog() ?? false,
      setReturnFocus: () => false,
      trapStack,
    });

    // Elm paints on an animation frame, so the panel is not here yet.
    this.activation = requestAnimationFrame(() => this.activate());
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.activation);
    this.observer.disconnect();
    this.removeEventListener("focusin", this.onFocusIn);

    // Before deactivate: it hands control to the trap underneath, which has to
    // be reachable by then.
    const index = openModals.indexOf(this);
    if (index !== -1) openModals.splice(index, 1);

    isolateTopmost();

    this.trap?.deactivate();
    this.trap = undefined;

    // Not while another modal still holds focus.
    if (this.heldFocus && !topmost() && this.returnFocusTo?.isConnected) {
      this.returnFocusTo.focus();
    }

    this.returnFocusTo = undefined;
    this.heldFocus = false;
  }

  activate() {
    const dialog = this.dialog();
    if (dialog) dialog.tabIndex = -1;

    this.trap?.activate();
  }

  // Ours, not a nested modal's.
  own(selector) {
    return [...this.querySelectorAll(selector)].filter((el) => el.closest("elm-modal") === this);
  }

  dialog() {
    return this.own('[role="dialog"]')[0] ?? null;
  }

  wire() {
    const dialog = this.dialog();
    if (!dialog) return;

    const [title] = this.own(TITLE);
    if (!title) {
      // One we did not mint is the caller's to keep.
      if (MANAGED_TITLE.test(dialog.getAttribute("aria-labelledby") ?? "")) {
        dialog.removeAttribute("aria-labelledby");
      }
      return;
    }

    title.id ||= `${this.modalId}-title`;
    dialog.setAttribute("aria-labelledby", title.id);
  }
}

if (!customElements.get("elm-modal")) {
  customElements.define("elm-modal", ElmModal);
}
